import { useState } from 'react';
import { Play } from 'lucide-react';

// Accepts a full YouTube URL (watch, youtu.be, shorts, embed, live) or a bare video ID
export const getYouTubeId = (url) => {
  if (!url) return '';
  const match = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/live\/)([A-Za-z0-9_-]{11})/);
  if (match) return match[1];
  return /^[A-Za-z0-9_-]{11}$/.test(url) ? url : '';
};

const isPreview = () => typeof window !== 'undefined' && window.location.hostname.includes('preview');

// Click-to-play facade: loads YouTube only when the visitor presses play
export const YouTubeVideo = ({ url, title, accent = '#7c3aed', testId, thumbVersion = '1', showPlayIcon = true }) => {
  const [playing, setPlaying] = useState(false);
  const id = getYouTubeId(url);

  if (!id) {
    if (!isPreview()) return null;
    return (
      <div className="aspect-video rounded-2xl border border-dashed border-white/20 bg-white/[0.03] flex flex-col items-center justify-center gap-2 text-center px-6" data-testid={testId}>
        <Play size={28} className="text-white/30" />
        <p className="text-white/50 text-sm">{title}</p>
        <p className="text-white/30 text-xs">Preview only: add the YouTube link in config/orbConfig.js (ORB_VIDEOS)</p>
      </div>
    );
  }

  if (playing) {
    return (
      <div className="aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black" data-testid={testId}>
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative block w-full aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black"
      aria-label={`Play video: ${title}`}
      data-testid={testId}
    >
      <img
        src={`https://i.ytimg.com/vi/${id}/maxresdefault.jpg?v=${thumbVersion}`}
        onError={(e) => { if (!e.currentTarget.src.includes('hqdefault')) e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg?v=${thumbVersion}`; }}
        alt={title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.02] transition-[opacity,transform] duration-500"
      />
      {showPlayIcon && <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />}
      {showPlayIcon && <span
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform duration-300"
        style={{ backgroundColor: accent, boxShadow: `0 0 40px ${accent}66` }}
      >
        <Play size={28} className="text-white ml-1" fill="white" />
      </span>}
    </button>
  );
};
